/**
 * Strip unknown sections / blocks from a `layout_blocks` payload on load.
 *
 * The Studio's validator rejects unknown sections (e.g. `express_checkout`
 * after the 2026-04-24 removal) at publish time. If a flow version was
 * saved with the now-removed shape still embedded, the merchant can't
 * republish until the stale data is cleaned. Rather than leave that as a
 * SQL-only repair path, we sanitize the layout as it enters the builder's
 * in-memory state so the next save writes clean JSON.
 *
 * `section_order` is filtered against the same allow-list — missing
 * sections quietly fall back to the canonical order.
 *
 * Called from PageBuilder.tsx's load effect + template-apply path.
 */
import type { BlockId, LayoutBlocks, SectionName } from "../types/flow";
import {
  VALID_BLOCKS_BY_SECTION,
  VALID_SECTIONS,
} from "../features/designer/validator";

function isAllowedBlock(section: string, id: string): boolean {
  // Custom HTML instances are allowed in any section.
  if (id.startsWith("custom_html_")) return true;
  const allowed = VALID_BLOCKS_BY_SECTION[section];
  return allowed !== undefined && allowed.has(id);
}

export function normalizeLayoutBlocks(layout: LayoutBlocks): LayoutBlocks {
  const cleanedSections: LayoutBlocks["sections"] = {} as LayoutBlocks["sections"];
  let mutated = false;

  for (const [name, cfg] of Object.entries(layout.sections) as Array<
    [string, LayoutBlocks["sections"][SectionName]]
  >) {
    if (!VALID_SECTIONS.has(name)) {
      mutated = true;
      continue;
    }
    const section = name as SectionName;
    const blocks = cfg?.blocks ?? [];
    const kept = blocks.filter((id) => isAllowedBlock(section, id));
    if (kept.length !== blocks.length) mutated = true;
    cleanedSections[section] = { ...cfg, blocks: kept as BlockId[] };
  }

  const cleanedOrder = layout.section_order?.filter((s): s is SectionName =>
    VALID_SECTIONS.has(s as string),
  );
  if (
    cleanedOrder &&
    layout.section_order &&
    cleanedOrder.length !== layout.section_order.length
  ) {
    mutated = true;
  }

  // Drop any block_rules / block_config entries that point at a removed
  // block id — they'd be dead data otherwise.
  let cleanedRules = layout.block_rules;
  if (cleanedRules) {
    const kept: Partial<Record<BlockId, LayoutBlocks["block_rules"] extends
      | Partial<Record<BlockId, infer R>>
      | undefined
      ? R
      : never>> = {};
    let rulesMutated = false;
    for (const [blockId, rule] of Object.entries(cleanedRules)) {
      const allowedAnywhere =
        blockId.startsWith("custom_html_") ||
        Object.values(VALID_BLOCKS_BY_SECTION).some((set) => set.has(blockId));
      if (allowedAnywhere) {
        (kept as Record<string, unknown>)[blockId] = rule;
      } else {
        rulesMutated = true;
      }
    }
    if (rulesMutated) {
      cleanedRules = kept as LayoutBlocks["block_rules"];
      mutated = true;
    }
  }

  if (!mutated) return layout;

  return {
    ...layout,
    sections: cleanedSections,
    section_order: cleanedOrder,
    block_rules: cleanedRules,
  };
}
